import classNames from 'classnames';
import moment from 'moment';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';

import { DatePicker } from '../../../components/DatePicker';

import * as api from '../../../api';

import PhoneInput from '../../../components/PhoneInput';
import { Email, isValidEmail } from '../../../components/Question/Email';
import { Button } from '../../../components/Button';
import { getMaybeAutocompletedDateOfBirth } from '../utils';
import { isNumberValid } from '../../../utils/phoneNumber';
import { getOptionsFromRecord } from '../../../components/utils';
import { SelectInput } from '../../../components/SelectInput';

import styles from '../index.module.css';

export type FormModel = Pick<
  api.Patient.Patient,
  'firstName' | 'lastName' | 'dateOfBirth' | 'cellPhoneNumber' | 'email'
> & {
  sex?: api.Common.Sex;
  reconciliationStatus?: api.Register.ReconciliationStatus;
};

export type FormModelReconciled = FormModel & {
  reconciliationStatus?: api.Register.ReconciliationStatus;
};

interface Props {
  handleSubmit: (values: FormModel) => Promise<void>;
  isSubmitting: boolean;
  formValues?: FormModel;
  submitButtonLabel?: string;
  additionalLoginInputs?: api.PracticePreference.PatientLoginInput[];
  previsitSexOptions?: api.Previsit.Sex[];
}

const PRIMITIVE_ENUM = api.PrevisitFormTemplateFieldConfig.Primitive;

const RegistrationForm: React.FC<Props> = ({
  handleSubmit,
  isSubmitting,
  formValues,
  submitButtonLabel,
  additionalLoginInputs,
  previsitSexOptions,
}) => {
  const { t } = useTranslation();
  const monthInput = useRef<HTMLInputElement>(null);
  const dayInput = useRef<HTMLInputElement>(null);
  const yearInput = useRef<HTMLInputElement>(null);

  const [firstName, setFirstName] = useState<string>(
    formValues?.firstName || '',
  );
  const [lastName, setLastName] = useState<string>(formValues?.lastName || '');
  const [dateOfBirth, setDateOfBirth] = useState<Date | undefined>(
    formValues?.dateOfBirth,
  );
  const [cellPhoneNumber, setCellPhoneNumber] = useState<string | null>(
    formValues?.cellPhoneNumber || null,
  );
  const [email, setEmail] = useState<string | null>(formValues?.email || null);
  const [sex, setSex] = useState<string | null>(formValues?.sex || null);

  const renderAdditionalFormInput = (
    primitive: api.PrevisitFormTemplateFieldConfig.Primitive,
  ) => {
    switch (primitive) {
      case PRIMITIVE_ENUM.PatientCellPhone:
        return (
          <div className={styles.InputGroup} key={primitive}>
            <label
              htmlFor="registration-cellPhoneNumber"
              className={styles.InputLabel}
            >
              {t('mobilePhoneNumber')}
            </label>
            <PhoneInput
              label={t('mobilePhoneNumber')}
              key="registration-cellPhoneNumber"
              className={styles.Input}
              value={cellPhoneNumber || ''}
              saveFieldValue={() => {}}
              updateFieldValue={setCellPhoneNumber}
            />
          </div>
        );
      case PRIMITIVE_ENUM.PatientSex:
        const DefaultSexOptions = _.pick(
          api.Previsit.sexOptionsMap,
          api.Previsit.DefaultSexes,
        );
        const filteredOptionsMap = previsitSexOptions?.length
          ? _.pick(api.Previsit.sexOptionsMap, previsitSexOptions)
          : DefaultSexOptions;
        const displayOptions = getOptionsFromRecord(filteredOptionsMap, t);
        return (
          <div className={styles.InputGroup} key={primitive}>
            <label htmlFor="registration-sex" className={styles.InputLabel}>
              {t('Sex')}
            </label>
            <SelectInput
              value={sex}
              options={displayOptions}
              onChange={(option) => setSex(option)}
            />
          </div>
        );
      case PRIMITIVE_ENUM.PatientEmail:
        return (
          <div className={styles.InputGroup} key={primitive}>
            <label htmlFor="registration-email" className={styles.InputLabel}>
              {_.capitalize(t('email'))}
            </label>
            <Email
              label={_.capitalize(t('email'))}
              key="registration-email"
              className={styles.Input}
              value={email || ''}
              saveFieldValue={() => {}}
              updateFieldValue={setEmail}
            />
          </div>
        );
      default:
        return <></>;
    }
  };

  const cellPhoneNumberRequired = additionalLoginInputs?.includes(
    PRIMITIVE_ENUM.PatientCellPhone,
  );
  const emailRequired = additionalLoginInputs?.includes(
    PRIMITIVE_ENUM.PatientEmail,
  );
  const sexRequired = additionalLoginInputs?.includes(
    PRIMITIVE_ENUM.PatientSex,
  );
  const toVerify = [
    dateOfBirth,
    firstName,
    lastName,
    ...(additionalLoginInputs?.length
      ? [
          cellPhoneNumberRequired ? cellPhoneNumber : true,
          emailRequired ? email : true,
        ]
      : []),
  ];
  const readyForLogin = toVerify.every((field) => !!field);

  const validateInputs = (payloadDob: Date): FormModel => {
    if (!payloadDob) {
      throw new Error(t('Please enter your date of birth'));
    }

    if (
      payloadDob > moment().startOf('day').tz('utc').locale('en').toDate() ||
      payloadDob <
        moment()
          .subtract(150, 'years')
          .startOf('day')
          .tz('utc')
          .locale('en')
          .toDate()
    ) {
      throw new Error(t('Please enter a valid date of birth'));
    }

    if (!lastName) {
      throw new Error(t('Please enter your last name'));
    }

    if (!firstName) {
      throw new Error(t('Please enter your first name'));
    }

    if (
      cellPhoneNumberRequired &&
      (!cellPhoneNumber || !isNumberValid(cellPhoneNumber))
    ) {
      throw new Error(t('Phone number is missing or invalid'));
    }

    if (emailRequired && (!email || !isValidEmail(email))) {
      throw new Error(t('Email is missing or invalid'));
    }
    if (sexRequired && !sex) {
      throw new Error(t('Sex is missing'));
    }
    return {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      dateOfBirth: payloadDob,
      ..._.omitBy({ cellPhoneNumber, email, sex }, _.isNil),
    };
  };

  const onSubmit = (evt: React.FormEvent<HTMLFormElement>) => {
    evt.preventDefault();
    evt.stopPropagation();

    const payloadDob =
      dateOfBirth ||
      getMaybeAutocompletedDateOfBirth({
        month: monthInput.current?.value,
        day: dayInput.current?.value,
        year: yearInput.current?.value,
      });

    if (!payloadDob) {
      alert(t('Please enter your date of birth'));
      return;
    }

    let formModel: FormModel;
    try {
      formModel = validateInputs(payloadDob);
    } catch (e) {
      alert(e.message);
      return;
    }
    handleSubmit(formModel);
  };

  return (
    <form onSubmit={onSubmit}>
      <div className={classNames(styles.Inputs, styles.RegistrationInputs)}>
        <div className={styles.InputGroup}>
          <label htmlFor="patient-first-name" className={styles.InputLabel}>
            {t('Legal first name')}
          </label>
          <input
            id="patient-first-name"
            className={styles.Input}
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.currentTarget.value)}
          />
        </div>
        <div className={styles.InputGroup}>
          <label htmlFor="patient-last-name" className={styles.InputLabel}>
            {t('Legal last name')}
          </label>
          <input
            id="patient-last-name"
            className={styles.Input}
            type="text"
            value={lastName}
            onChange={(e) => setLastName(e.currentTarget.value)}
          />
        </div>
        <div
          className={classNames(
            styles.InputGroup,
            styles.RegistrationDatePickerInput,
          )}
        >
          <label htmlFor="patient-last-name" className={styles.InputLabel}>
            {t('Date of birth')}
          </label>
          <DatePicker
            className={styles.DatePicker}
            isLogin={true}
            isRegistration={true}
            onChange={setDateOfBirth}
            monthInput={monthInput}
            dayInput={dayInput}
            yearInput={yearInput}
            date={
              dateOfBirth && !isNaN(new Date(dateOfBirth).valueOf())
                ? new Date(dateOfBirth).toISOString()
                : ''
            }
          />
        </div>
        {additionalLoginInputs?.map(renderAdditionalFormInput)}
      </div>
      <Button
        id="sign-in-button"
        children={submitButtonLabel || t('Continue')}
        type="submit"
        disabled={!readyForLogin || isSubmitting}
      />
    </form>
  );
};

export default RegistrationForm;
